import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useMemo, useRef } from "react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";

import {
    applyCardTypeReadOnlyFieldsUtils,
    keepCardTypeReadOnlyFlagsUtils,
    stripDialogProfileCheckReadOnlyFields,
    updateWatchedFieldsUtils,
} from "src/app/features/ApplicationProfile/CardProfileUtils";
import {
    useCardProfileUpdateMutation,
    useLazyCardProfileLoadQuery,
} from "src/app/features/ApplicationProfile/subFeatures/CardProfile/services/server/CardProfileApi";
import type { CardProfileQueryParams } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/services/server/types";
import type { CardProfileType } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileType";
import { useCardTypeLoadQuery } from "src/app/features/CardType/services/server/CardTypeApi";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { InheritedFieldChangeConfirmationModal } from "../../../../../../../components/Modal/InheritedFieldChangeConfirmationModal";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import type { ID } from "../../../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../../../core/utils/utilFunctions";
import type { CardProfileEntity } from "../../types/CardProfileEntity";
import { CardProfileFormConfigMap } from "./CardProfileFormConfig";

export const CardProfileEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { appProfileId, id } = useParams();
    const [searchParams] = useSearchParams();

    const type = searchParams.get("type") as CardProfileType;
    const location = useLocation();
    const isApac = location.pathname.includes("/apac");

    const formNameConfigs = isApac ? CardProfileFormConfigMap.forms.apac : CardProfileFormConfigMap.forms.eft4f;
    const formDefinitionString = formNameConfigs[type];

    const backUrl = isApac
        ? `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${appProfileId}`
        : `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${FORM_ACTION.EDIT}/${appProfileId}`;
    const completeSubmit = useFormSubmitCompletion(formAction, backUrl);

    // set save params
    useEffect(() => {
        setQueryConfig({ appProfileId, id });
    }, [appProfileId, id]);

    // load form definition
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery(formDefinitionString as string);
    const [loadFormValues, { isFetching: formValueIsFetching }] = useLazyCardProfileLoadQuery();

    // get card type id config
    const BASIC_PARAMS = 0;
    const cardTypeIdConfig = useMemo(
        () => formDefinition?.formConfiguration?.tabs[BASIC_PARAMS].fields?.find((item) => item.name === "cardTypeId"),
        [formDefinition],
    );
    const updateFields = cardTypeIdConfig?.updateFields;

    // manage form data
    const [formData, setFormData] = useState<CardProfileEntity>({} as CardProfileEntity);
    const prevProfileIdRef = useRef<ID | undefined>(undefined);
    const prevDialogProfileIdRef = useRef<ID | undefined>(undefined);
    const pendingFormDataRef = useRef<CardProfileEntity | null>(null);
    const formDataRef = useRef(formData);
    const [cardTypeId, setCardTypeId] = useState<ID | undefined>(undefined);
    const [dialogProfileId, setDialogProfileId] = useState<ID | undefined>(undefined);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    // manage submit action
    const [updateFormData, { isLoading: updateFormIsInProgress }] = useCardProfileUpdateMutation();
    const [queryConfig, setQueryConfig] = useState<CardProfileQueryParams>({});

    // load form data
    useEffect(() => {
        if (id && queryConfig.appProfileId) {
            loadFormValues(queryConfig).then((data) => {
                if (data.data) {
                    // Ensure dialogVehicleIdFormat has a value at load time
                    const loadedData = data.data.dialogVehicleIdFormat ? data.data : { ...data.data, dialogVehicleIdFormat: "UNDEFINED" };
                    setFormData(loadedData);
                    formDataRef.current = loadedData;
                    prevProfileIdRef.current = loadedData.cardTypeId;
                    prevDialogProfileIdRef.current = loadedData.dialogProfileId;
                    setCardTypeId(loadedData.cardTypeId);
                    setDialogProfileId(loadedData.dialogProfileId);
                }
            });
        }
    }, [id, loadFormValues, queryConfig]);

    const applyUpdatedData = useCallback((updatedData: CardProfileEntity) => {
        formDataRef.current = updatedData;

        if (updatedData.cardTypeId !== prevProfileIdRef.current) {
            setCardTypeId(updatedData.cardTypeId);
            setFormData(updatedData);

            if (updatedData.cardTypeId === undefined) {
                prevProfileIdRef.current = undefined;
            }
        }

        if (updatedData.dialogProfileId !== prevDialogProfileIdRef.current) {
            setDialogProfileId(updatedData.dialogProfileId);
        }
    }, []);

    // HANDLE ON CHANGE — ref-only for normal typing, state only for structural field changes
    const onChangeCallback = useCallback(
        (newData: CardProfileEntity) => {
            const updatedData = keepCardTypeReadOnlyFlagsUtils(newData, formDataRef.current, updateFields);

            const hasCardTypeChange = updatedData.cardTypeId !== prevProfileIdRef.current;
            if (hasCardTypeChange && updatedData.cardTypeId !== undefined) {
                pendingFormDataRef.current = updatedData;
                setIsConfirmModalOpen(true);
                setFormData({ ...formDataRef.current });
                return;
            }

            applyUpdatedData(updatedData);
        },
        [applyUpdatedData, updateFields],
    );

    const onConfirmCardTypeChange = useCallback(() => {
        const pendingData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        if (pendingData) {
            applyUpdatedData(pendingData);
        }
    }, [applyUpdatedData]);

    const onCancelCardTypeChange = useCallback(() => {
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        setFormData({ ...formDataRef.current });
    }, []);

    // HANDLE WATCH FIELDS — cardTypeId
    const { data: cardTypeEntity } = useCardTypeLoadQuery({ entityId: `${cardTypeId}` }, { skip: !cardTypeId });

    const updateWatchedFields = useCallback(() => {
        const currentProfileId = formDataRef.current?.cardTypeId;

        if (!currentProfileId || !cardTypeEntity || !updateFields || String(cardTypeEntity.id) !== String(currentProfileId)) return null;

        prevProfileIdRef.current = currentProfileId;

        if (!formDataRef.current) return null;

        return updateWatchedFieldsUtils(cardTypeEntity, formDataRef.current, updateFields);
    }, [cardTypeEntity, updateFields]);

    useEffect(() => {
        if (!cardTypeEntity || !cardTypeId || String(cardTypeEntity.id) !== String(cardTypeId)) {
            return;
        }

        const prevProfileId = prevProfileIdRef.current;

        if (cardTypeId !== prevProfileId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [cardTypeId, cardTypeEntity, updateWatchedFields]);

    // HANDLE LOADED CARD PROFILE — lock the fields that are read-only in its card type
    const readOnlyAppliedForCardTypeIdRef = useRef<ID | undefined>(undefined);
    useEffect(() => {
        if (!cardTypeEntity || !cardTypeId || !updateFields || String(cardTypeEntity.id) !== String(cardTypeId)) {
            return;
        }
        // a changed card type is handled by updateWatchedFields above
        if (cardTypeId !== prevProfileIdRef.current || readOnlyAppliedForCardTypeIdRef.current === cardTypeId) {
            return;
        }
        readOnlyAppliedForCardTypeIdRef.current = cardTypeId;

        const newFormData = applyCardTypeReadOnlyFieldsUtils(cardTypeEntity, formDataRef.current, updateFields);
        if (!isTheSameByDeepCompare(formDataRef.current, newFormData)) {
            setFormData(newFormData);
            formDataRef.current = newFormData;
        }
    }, [cardTypeId, cardTypeEntity, updateFields]);

    // HANDLE WATCH FIELDS — dialogProfileId
    useEffect(() => {
        const currentDialogProfileId = dialogProfileId;
        const prevDialogProfileId = prevDialogProfileIdRef.current;
        if (currentDialogProfileId !== prevDialogProfileId) {
            prevDialogProfileIdRef.current = currentDialogProfileId;
            const isEmpty = currentDialogProfileId === null || currentDialogProfileId === undefined;
            const updatedFormData = isEmpty
                ? {
                      ...formDataRef.current,
                      dialogsYesCheckReadOnly: false,
                      dialogsNoCheckReadOnly: false,
                      dialogsMandatoryCheckReadOnly: false,
                      dialogsCheckCheckReadOnly: false,
                      dialogVehicleIdFormatCheckReadOnly: false,
                  }
                : {
                      ...formDataRef.current,
                      dialogsYes: undefined,
                      dialogsNo: undefined,
                      dialogsMandatory: undefined,
                      dialogsCheck: undefined,
                      dialogVehicleIdFormat: "UNDEFINED",
                      dialogsYesCheckReadOnly: true,
                      dialogsNoCheckReadOnly: true,
                      dialogsMandatoryCheckReadOnly: true,
                      dialogsCheckCheckReadOnly: true,
                      dialogVehicleIdFormatCheckReadOnly: true,
                  };
            if (!isTheSameByDeepCompare(formDataRef.current, updatedFormData as CardProfileEntity)) {
                setFormData(updatedFormData as CardProfileEntity);
                formDataRef.current = updatedFormData as CardProfileEntity;
            }
        }
    }, [dialogProfileId]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            const currentFormData = formDataRef.current;
            if (!valid || !currentFormData) {
                return;
            }

            const sanitizedFormData = stripDialogProfileCheckReadOnlyFields(currentFormData);
            const result = await updateFormData({
                cardProfilesFormData: sanitizedFormData,
                cardProfileQuery: queryConfig,
            });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formUpdateSuccess", {
                        id: result.data.id,
                        resource: "Card Profile",
                    }),
                );
            }
        },
        [updateFormData, queryConfig, completeSubmit],
    );

    // Memoize formData prop so its reference only changes when content changes.
    // Without this, any parent re-render (e.g. runSubmit change, RTK Query refetch)
    // creates a new object reference that triggers FormManager's useEffect,
    // which resets Ant Form fields back to stale state — wiping user overrides.
    const formManagerData = useMemo(() => (type ? { ...formData, id, type } : { ...formData, id }), [formData, id, type]);

    return (
        <Spin spinning={updateFormIsInProgress || configIsFetching || formValueIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardProfileEntity>
                    name={formDefinition.formConfiguration.resource}
                    formData={formManagerData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <InheritedFieldChangeConfirmationModal
                open={isConfirmModalOpen}
                onConfirm={onConfirmCardTypeChange}
                onCancel={onCancelCardTypeChange}
            />
        </Spin>
    );
};
